import React, { type ReactNode, useCallback, useEffect, useMemo } from 'react';
import { ElementTypes, MultiPageVideoModel } from '@bynder-studio/render-core';
import { useSelector } from 'react-redux';
import { IconCancel } from '@bynder/icons';
import { Button, Inspector, Tabs } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import CreativeTypes from 'packages/helpers/CreativeTypes';
import features from 'packages/configs/features';
import generateTestId from 'packages/helpers/testIdHelpers';
import useAccessRights from 'packages/hooks/useAccessRights';
import { useReviewStatus } from 'packages/pages/design/sidebar/shots/useReviewStatus';
import ShotAudio from 'packages/pages/design/sidebar/shots/ShotAudio';
import Variations from './variations/Variations';
import ResetPage from './variations/ResetPage';
import { DesignTab } from './types';
import Transcript from 'packages/pages/components/Transcript';
import useDesign from 'packages/pages/design/hooks/useDesign';
import useVariations from 'packages/pages/design/hooks/useVariations';

type Props = {
    children: ReactNode;
    designTab: DesignTab;
    setDesignTab: (tab: DesignTab) => void;
};

const Sidebar = ({ children, designTab, setDesignTab }: Props) => {
    const { translate } = useTranslate();
    const { isPlatformAllowed } = useAccessRights();
    const { isReviewer, isInReviewMode, isEditsRequestedMode, isCollaborator, isInApprovedMode } = useReviewStatus();
    const { creativeType } = useSelector((state) => ({
        // @ts-ignore
        creativeType: state.creatives.creative.creativeType,
    }));
    const { creativeModel, template, canvasRenderer } = useDesign();
    const { currentVariation, variations, updateVariationGlobalProperty, saveVariation } = useVariations();
    const isMultiVariationAvailable = useMemo(() => isPlatformAllowed([features.MULTI_VARIATION]), [isPlatformAllowed]);

    // Resolve the per-page variation ("size") id for the currently active page.
    // Reads the live `variations` array so it reflects in-place mutations done by
    // `saveVariation` (which reassigns `variation.id`/`variation.sizes`). We match by
    // both `id` and `oldId` because a save swaps the variation-set id.
    const resolveVariationPageId = useCallback(
        (variationSetId: string) => {
            const variationData = variations?.find(
                (variation) => variation.id === variationSetId || variation.oldId === variationSetId,
            );

            if (!variationData) {
                return;
            }

            const currentPageId = creativeModel?.getCurrentPageId?.();
            const activePageSize =
                currentPageId !== undefined && currentPageId !== null
                    ? variationData.sizes?.[currentPageId] || variationData.sizes?.[String(currentPageId)]
                    : undefined;

            if (activePageSize?.variationId) {
                return activePageSize.variationId;
            }

            const firstSize = Object.values(variationData.sizes || {})[0] as { variationId?: string } | undefined;

            return firstSize?.variationId;
        },
        [creativeModel, variations],
    );

    // Save the variation and return the FRESH per-page variation id. `saveVariation`
    // mutates the variation in place (new set id + new per-size ids), so callers that
    // need the saved id must re-derive it *after* the promise resolves rather than
    // relying on a value captured before the save.
    const handleSaveVariation = useCallback(
        async (variationSetId: string, callback?: () => void, resetPages?: number[]) => {
            await saveVariation(variationSetId, callback, resetPages);

            return resolveVariationPageId(variationSetId);
        },
        [saveVariation, resolveVariationPageId],
    );

    const activeVariationId = useMemo(() => {
        if (!currentVariation) {
            return;
        }

        return resolveVariationPageId(currentVariation);
    }, [resolveVariationPageId, currentVariation]);

    const isInitialTemplateTranscriptEnabled = useMemo(() => {
        const pages = template?.pages;
        const currentPageIndex = creativeModel?.getCurrentPageIndex?.();

        if (!Array.isArray(pages) || typeof currentPageIndex !== 'number' || currentPageIndex < 0) {
            return false;
        }

        const activePage = pages[currentPageIndex];

        if (!activePage || typeof activePage !== 'object') {
            return false;
        }

        const elementsValue =
            (activePage as { globalElements?: unknown; elements?: unknown }).globalElements ||
            (activePage as { globalElements?: unknown; elements?: unknown }).elements;

        if (!elementsValue) {
            return false;
        }

        const elements = Array.isArray(elementsValue) ? elementsValue : Object.values(elementsValue as object);

        return elements.some((element) => {
            if (!element || typeof element !== 'object') {
                return false;
            }

            const elementRecord = element as { type?: string; properties?: { enabled?: boolean }; enabled?: boolean };

            if (elementRecord.type !== 'TRANSCRIPT') {
                return false;
            }

            return Boolean(elementRecord.properties?.enabled ?? elementRecord.enabled);
        });
    }, [creativeModel, template]);

    useEffect(() => {
        const currentModel = (creativeModel as any)?.getCurrentModel?.();

        if (!currentModel?.eventEmitter || !currentVariation) {
            return;
        }

        const handleTranscriptUpdated = ({ property, newValues }) => {
            if (!property?.id) {
                return;
            }

            updateVariationGlobalProperty(ElementTypes.TRANSCRIPT, property.id, newValues);
        };

        currentModel.eventEmitter.on('transcriptUpdated', handleTranscriptUpdated);

        return () => {
            currentModel.eventEmitter.off('transcriptUpdated', handleTranscriptUpdated);
        };
    }, [creativeModel, currentVariation, updateVariationGlobalProperty]);

    const showResetButton = useMemo(() => {
        if (designTab !== DesignTab.SCENE) {
            return false;
        }

        if (isReviewer) {
            return isCollaborator && !isInApprovedMode && !isInReviewMode;
        }

        return !isInApprovedMode;
    }, [designTab, isReviewer, isCollaborator, isEditsRequestedMode, isInApprovedMode, isInReviewMode]);

    return (
        <Inspector {...generateTestId('design_configuration_sidebar')}>
            {isMultiVariationAvailable && designTab !== DesignTab.AUDIO && designTab !== DesignTab.TRANSCRIPT && (
                <Inspector.Tabs>
                    <Tabs>
                        <>
                            <Tabs.Tab
                                label={
                                    creativeType === CreativeTypes.Video
                                        ? translate('design.sidebar.scene')
                                        : translate('design.sidebar.image')
                                }
                                isActive={designTab === DesignTab.SCENE}
                                onClick={() => {
                                    setDesignTab(DesignTab.SCENE);
                                }}
                            />
                            <Tabs.Tab
                                label={translate('design.sidebar.variations')}
                                isActive={designTab === DesignTab.VARIATIONS}
                                onClick={() => {
                                    setDesignTab(DesignTab.VARIATIONS);
                                }}
                            />
                        </>
                    </Tabs>
                </Inspector.Tabs>
            )}

            {(designTab === DesignTab.AUDIO || designTab === DesignTab.TRANSCRIPT) && (
                <>
                    <Inspector.Header
                        title={
                            designTab === DesignTab.AUDIO
                                ? translate('design.sidebar.audio')
                                : translate('design.sidebar.transcript')
                        }
                        rightElements={
                            <Button
                                title=""
                                variant="clean"
                                icon={<IconCancel />}
                                onClick={() => {
                                    setDesignTab(DesignTab.SCENE);
                                }}
                            />
                        }
                    />
                    <Inspector.Divider />
                </>
            )}

            <Inspector.Content>
                {designTab === DesignTab.SCENE && children}
                {designTab === DesignTab.AUDIO && (
                    <>
                        <ShotAudio track={1} />
                        <ShotAudio track={2} />
                    </>
                )}
                {designTab === DesignTab.TRANSCRIPT && (
                    <Transcript
                        creativeModel={creativeModel as MultiPageVideoModel}
                        onBack={() => {
                            setDesignTab(DesignTab.SCENE);
                        }}
                        showEnabledToggle={false}
                        showStyles={false}
                        showBackButton={false}
                        context="content-configurator"
                        variationPageId={activeVariationId}
                        variationId={currentVariation}
                        pageIndex={creativeModel?.getCurrentPageIndex?.()}
                        onSaveVariation={handleSaveVariation}
                        isInitialTemplateTranscriptEnabled={isInitialTemplateTranscriptEnabled}
                        manipulationRenderer={canvasRenderer}
                    />
                )}
                {designTab === DesignTab.VARIATIONS && <Variations />}
            </Inspector.Content>
            {showResetButton && <ResetPage />}
        </Inspector>
    );
};

export default Sidebar;
